<script lang="ts" setup>

import { reactive, computed } from "vue";

import * as relatoriosService from "@/services/relatoriosService";
import { fdata } from "@/utils/funcoes";
import { useOrdenacao } from "@/composables/useOrdenacao";

import BreadCrumb from "@/components/Common/BreadCrumb.vue";
import Notificacao from "@/components/Notificacao/Index.vue";
import TrataTecla from "@/components/TrataTecla/Index.vue";
import DropdownVendedores from "@/components/Dropdowns/DropdownVendedores.vue";
import DropdownPlanos from "@/components/Dropdowns/DropdownPlanos.vue";
import ReportView from "@/components/ReportView/Index.vue";
import ReportCabecalho from "@/components/ReportView/Cabecalho.vue";

const colunasOpcionais = [
  { chave: "nome", label: "Nome" },
  { chave: "cpf", label: "CPF" },
  { chave: "nomeConjuge", label: "Nome Cônjuge" },
  { chave: "cpfConjuge", label: "CPF Cônjuge" },
  { chave: "parentesco", label: "Parentesco" },
  { chave: "nomeDependente", label: "Nome Dependente" },
  { chave: "nascimentoDependente", label: "Nascimento" },
  { chave: "cpfDependente", label: "CPF Depend" },
  { chave: "telefoneDependente", label: "Telefone" },
];

const controle = reactive({
  filtros: {
    vendedorId: null,
    planoId: null,
    ativo: true,
    dataCadastroInicial: null,
    dataCadastroFinal: null,
  } as relatoriosService.FiltroClientesDependentes,

  itens: [] as relatoriosService.RelClienteDependenteDTO[],
  totalRegistros: 0,

  colunasVisiveis: {
    nome: true,
    cpf: true,
    nomeConjuge: true,
    cpfConjuge: true,
    parentesco: true,
    nomeDependente: true,
    nascimentoDependente: true,
    cpfDependente: true,
    telefoneDependente: false,
  } as Record<string, boolean>,

  erro: "",
  status: "",
  pesquisando: false,
});

async function pesquisar() {
  controle.pesquisando = true;
  controle.erro = "";
  try {
    const r = await relatoriosService.clientesDependentes(controle.filtros);
    controle.totalRegistros = r.totalRegistros;
    controle.itens = r.dados;
  } catch (e: any) {
    controle.erro = e?.message || "Erro ao pesquisar";
  } finally {
    controle.pesquisando = false;
  }
}

const sort = useOrdenacao();

const itensOrdenados = computed(() => sort.aplicar(controle.itens, {
  id: (item: relatoriosService.RelClienteDependenteDTO) => item.clienteId,
  nascimentoDependente: (item: relatoriosService.RelClienteDependenteDTO) => item.dataNascimentoDependente || "",
}));

</script>

<template>
  <BreadCrumb PageTitle="Clientes X Dependentes" :items="[
    { label: 'Relatórios', to: '/relatorios' },
    { label: 'Clientes X Dependentes', to: '/relatorios/clientesdependentes' }]" />

  <Notificacao :erro="controle.erro" @limparErro="controle.erro = ''" :progresso="controle.status"
    @limparProgresso="controle.status = ''" />

  <TrataTecla>
    <div class="card mb-2 d-print-none">
      <div class="card-body">
        <div class="row">

          <div class="col-md-2 col-6">
            <label>Vendedor</label>
            <DropdownVendedores v-model="controle.filtros.vendedorId" id="vendedorId" />
          </div>

          <div class="col-md-2 col-6">
            <label>Plano</label>
            <DropdownPlanos v-model="controle.filtros.planoId" id="planoId" />
          </div>

          <div class="col-md-2 col-6">
            <label>Dt Cad Ini</label>
            <input v-model="controle.filtros.dataCadastroInicial" type="date" class="form-control form-control-sm" id="dataCadastroInicial" />
          </div>

          <div class="col-md-2 col-6">
            <label>Dt Cad Fin</label>
            <input v-model="controle.filtros.dataCadastroFinal" type="date" class="form-control form-control-sm" id="dataCadastroFinal" />
          </div>

          <div class="col-md-2 col-6">
            <label>Ativo</label>
            <select v-model="controle.filtros.ativo" class="form-select form-select-sm" id="ativo">
              <option :value="null">Todos</option>
              <option :value="true">Ativos</option>
              <option :value="false">Inativos</option>
            </select>
          </div>

          <div class="col-md-2 col-6">
            <a class="btn btn-primary btn-sm mt-4" @click="pesquisar">
              <i class="fa fa-search me-1"></i> Pesquisar
            </a>
          </div>

          <div class="col-md-2 col-6">
            <div class="dropdown">
              <a class="btn btn-outline-secondary btn-sm dropdown-toggle mt-4" href="#" role="button"
                data-bs-toggle="dropdown" aria-expanded="false">
                <i class="fa fa-table-columns me-1"></i> Colunas
              </a>
              <ul class="dropdown-menu p-2" style="min-width: 200px;">
                <li v-for="col in colunasOpcionais" :key="col.chave">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" :id="'col_' + col.chave"
                      v-model="controle.colunasVisiveis[col.chave]">
                    <label class="form-check-label" :for="'col_' + col.chave">{{ col.label }}</label>
                  </div>
                </li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="col-md-12">

      <ReportView :titulo="'Rel Clientes X Dependentes'" :btnImprimir="true" :orientacao="'Paisagem'">

        <div v-if="controle.erro != ''" class="col-md-12 text-center">
          <div class="p-4 text-danger fw-14">{{ controle.erro }}</div>
        </div>

        <div v-if="controle.erro == '' && controle.pesquisando" class="col-md-12 text-center">
          <div class="p-4">
            <i class="fa fa-cog fa-2x fa-spin"></i>
            <div class="fw-16">Pesquisando...</div>
          </div>
        </div>

        <template v-if="controle.erro == '' && !controle.pesquisando && controle.itens.length > 0">

          <ReportCabecalho :Titulo="'Relatório de Clientes X Dependentes'" :Filtros="`Ativo: ${controle.filtros.ativo === true ? 'Ativos' : controle.filtros.ativo === false ? 'Inativos' : 'Todos'}  |  Dt Cadastro: ${controle.filtros.dataCadastroInicial ? fdata(controle.filtros.dataCadastroInicial) : '...'} a ${controle.filtros.dataCadastroFinal ? fdata(controle.filtros.dataCadastroFinal) : '...'}`" />

          <div class="mb-1 text-muted f12px">Total: {{ controle.itens.length }} registro(s)</div>

          <div style="overflow-x: auto;">
          <table class="tabela table-sm table-bordered f12px" id="tabela-relatorio">
            <thead>
              <tr class="table-relatorio">
                <th class="text-center colId col-ordenavel" @click="sort.alternar('id')">Id <i :class="sort.classeIcone('id')"></i></th>
                <th v-if="controle.colunasVisiveis.nome" class="text-start colNome col-ordenavel" @click="sort.alternar('nome')">Nome <i :class="sort.classeIcone('nome')"></i></th>
                <th v-if="controle.colunasVisiveis.cpf" class="text-center colCpf col-ordenavel" @click="sort.alternar('cpf')">CPF <i :class="sort.classeIcone('cpf')"></i></th>
                <th v-if="controle.colunasVisiveis.nomeConjuge" class="text-start colNomeConj col-ordenavel" @click="sort.alternar('nomeConjuge')">Nome Cônjuge <i :class="sort.classeIcone('nomeConjuge')"></i></th>
                <th v-if="controle.colunasVisiveis.cpfConjuge" class="text-center colCpf col-ordenavel" @click="sort.alternar('cpfConjuge')">CPF Cônjuge <i :class="sort.classeIcone('cpfConjuge')"></i></th>
                <th v-if="controle.colunasVisiveis.parentesco" class="text-start colTipo col-ordenavel" @click="sort.alternar('parentesco')">Tipo <i :class="sort.classeIcone('parentesco')"></i></th>
                <th v-if="controle.colunasVisiveis.nomeDependente" class="text-start colNomeDepend col-ordenavel" @click="sort.alternar('nomeDependente')">Dependente <i :class="sort.classeIcone('nomeDependente')"></i></th>
                <th v-if="controle.colunasVisiveis.nascimentoDependente" class="text-center colData col-ordenavel" @click="sort.alternar('nascimentoDependente')">Nascto <i :class="sort.classeIcone('nascimentoDependente')"></i></th>
                <th v-if="controle.colunasVisiveis.cpfDependente" class="text-center colCpf col-ordenavel" @click="sort.alternar('cpfDependente')">CPF <i :class="sort.classeIcone('cpfDependente')"></i></th>
                <th v-if="controle.colunasVisiveis.telefoneDependente" class="text-center colTelefone col-ordenavel" @click="sort.alternar('telefoneDependente')">Telefone <i :class="sort.classeIcone('telefoneDependente')"></i></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in itensOrdenados" :key="idx">
                <td class="text-center  colId">{{ item.clienteId }}</td>
                <td v-if="controle.colunasVisiveis.nome" class="text-start colNome">{{ item.nome }}</td>
                <td v-if="controle.colunasVisiveis.cpf" class="text-center colCpf">{{ item.cpf }}</td>
                <td v-if="controle.colunasVisiveis.nomeConjuge" class="text-start colNomeConj">{{ item.nomeConjuge ?? '-' }}</td>
                <td v-if="controle.colunasVisiveis.cpfConjuge" class="text-center colCpf">{{ item.cpfConjuge ?? '-' }}</td>
                <td v-if="controle.colunasVisiveis.parentesco" class="text-start">{{ item.parentesco ?? '-' }}</td>
                <td v-if="controle.colunasVisiveis.nomeDependente" class="text-start colNomeDepend">{{ item.nomeDependente ?? '-' }}</td>
                <td v-if="controle.colunasVisiveis.nascimentoDependente" class="text-center colData">{{ item.dataNascimentoDependente ? fdata(item.dataNascimentoDependente) : '-' }}</td>
                <td v-if="controle.colunasVisiveis.cpfDependente" class="text-center colCpf">{{ item.cpfDependente ?? '-' }}</td>
                <td v-if="controle.colunasVisiveis.telefoneDependente" class="text-center colTelefone">{{ item.telefoneDependente ?? '-' }}</td>
              </tr>
            </tbody>
          </table>
          </div>

        </template>

        <template v-if="controle.erro == '' && !controle.pesquisando && controle.itens.length === 0 && controle.totalRegistros === 0 && controle.status === ''">
          <div class="text-center p-4 text-muted">
            Utilize os filtros acima e clique em <strong>Pesquisar</strong> para gerar o relatório.
          </div>
        </template>

        <template v-if="controle.erro == '' && !controle.pesquisando && controle.itens.length === 0 && controle.totalRegistros > 0">
          <div class="text-center p-4 text-muted">Nenhum registro encontrado com os filtros selecionados.</div>
        </template>

      </ReportView>

    </div>
  </TrataTecla>
</template>

<style lang="scss" scoped>

#tabela-relatorio {
  table-layout: fixed;
  min-width: 1200px;
  width: 100%;
}
#tabela-relatorio th,
#tabela-relatorio td {
  font-size: 12px !important;
}

@media print {
  #tabela-relatorio {
    min-width: 0;
  }
}

.colId {
  width: 40px;
}
.colNome {
  width: auto;
}
.colCpf {
  width: 100px;
}
.colNomeConj {
  width: auto;
}
.colTipo {
  width: 60px;
}
.colNomeDepend {
  width: auto;
}
.colData {
  width: 77px;
}
.colTelefone {
  width: 100px;
}

</style>
